<template>
    <div class="platform-login" :class="{'platform-login--embedded': embedded}">
        <section class="platform-login__intro">
            <div class="platform-login__intro-content">
                <div class="platform-login__eyebrow">ARGUS CONTROL PLANE</div>
                <h1>Платформа<br>под контролем</h1>
                <p>
                    Единая точка для инцидентов, состояния компаний и действий,
                    требующих внимания администратора проекта.
                </p>
                <div class="platform-login__scope">
                    <span>Отдельный контур доступа</span>
                    <span>Над компаниями</span>
                    <span>Подтверждение инцидентов</span>
                </div>
            </div>
        </section>

        <section class="platform-login__form-side">
            <TelegramChallenge v-if="challenge" :initial="challenge" scope="platform" :password="form.password" @authenticated="complete" @cancel="challenge = null"/>
            <form v-else class="platform-login__card" @submit.prevent="submit()">
                <div class="platform-login__mark">A</div>
                <div class="platform-login__caption">Платформа Argus</div>
                <h2>Вход администратора</h2>
                <p class="platform-login__hint">
                    Учётная запись платформы не связана с пользователями отдельных компаний.
                </p>

                <div v-if="displayedErrorMessage" class="alert alert-danger" role="alert">
                    {{ displayedErrorMessage }}
                </div>

                <label class="platform-login__field">
                    <span>Логин</span>
                    <input
                        v-model.trim="form.login"
                        type="text"
                        autocomplete="username"
                        autofocus
                        required
                        :disabled="submitting"
                    >
                </label>
                <label class="platform-login__field">
                    <span>Пароль</span>
                    <input
                        v-model="form.password"
                        type="password"
                        autocomplete="current-password"
                        required
                        :disabled="submitting"
                    >
                </label>

                <ForeignDeviceOption v-model="foreignDevice"/>
                <button
                    class="platform-login__submit"
                    type="submit"
                    :disabled="submitting || cooldownActive"
                >
                    <span v-if="submitting" class="spinner-border spinner-border-sm mr-2"></span>
                    {{ submitButtonText }}
                </button>
                <div class="mt-3"><vue-telegram-login mode="callback" :telegram-login="bot" @callback="telegramLogin"/></div>
                <router-link class="platform-login__tenant-link" to="/login">
                    Вход сотрудника компании
                </router-link>
            </form>
        </section>
    </div>
</template>

<script>
import {vueTelegramLogin} from 'vue-telegram-login';
import TelegramChallenge from '@/components/Security/TelegramChallenge.vue';
import ForeignDeviceOption from '@/components/Security/ForeignDeviceOption.vue';
import {acceptSession, securityRequest} from '@/utils/sessionSecurity';
import {isPlatformAccessTokenValid, setPlatformSession} from '@/platform/session';
import {
    cooldownDeadlineFromError,
    cooldownSecondsRemaining,
    formatCooldown
} from '@/utils/loginCooldown';

export default {
    name: 'PlatformLogin',
    props: {embedded: Boolean},
    components: {vueTelegramLogin, TelegramChallenge, ForeignDeviceOption},
    data() {
        return {
            form: {login: '', password: ''}, foreignDevice: false, challenge: null,
            bot: process.env.VUE_APP_TELEGRAM_LOGIN_BOT || 'ArgusVision_Bot',
            submitting: false,
            errorMessage: '',
            cooldownIdentity: null,
            cooldownUntil: 0,
            cooldownSeconds: 0,
            cooldownTimer: null
        };
    },
    computed: {
        currentIdentity() {
            return String(this.form.login || '').trim().toLowerCase();
        },
        cooldownActive() {
            return this.cooldownSeconds > 0 && this.cooldownIdentity === this.currentIdentity;
        },
        cooldownText() {
            return formatCooldown(this.cooldownSeconds);
        },
        displayedErrorMessage() {
            return this.cooldownActive
                ? `Слишком много неудачных попыток. Повторите через ${this.cooldownText}.`
                : this.errorMessage;
        },
        submitButtonText() {
            if (this.submitting) return 'Проверяем…';
            return this.cooldownActive ? `Повторить через ${this.cooldownText}` : 'Войти в платформу';
        }
    },
    mounted() {
        if (!this.embedded && isPlatformAccessTokenValid()) this.$router.replace('/platform');
    },
    beforeDestroy() {
        this.stopCooldownTimer();
    },
    methods: {
        safeRedirect() {
            const redirect = String(this.$route.query.redirect || '');
            return redirect.startsWith('/platform') && !redirect.startsWith('//')
                ? redirect
                : '/platform';
        },
        telegramLogin(tgUser) { return this.submit({tgUser}); },
        complete(session) {
            if (session.challenge) { this.challenge = session; return; }
            try { acceptSession('platform', session, true); } catch (error) { this.errorMessage = error.message; this.challenge = null; return; }
            setPlatformSession(session);
            if (this.embedded) this.$emit('authenticated');
            else this.$router.replace(this.safeRedirect());
        },
        async submit(payload) {
            if (this.cooldownActive) return;
            this.submitting = true;
            this.errorMessage = '';
            try {
                this.complete(await securityRequest('platform', 'login', Object.assign({}, payload || this.form, {personal: !this.foreignDevice})));
            } catch (error) {
                if (error.response && error.response.status === 429) {
                    this.startCooldown(error);
                } else {
                    this.errorMessage = error.response && error.response.data && error.response.data.msg
                        ? error.response.data.msg
                        : 'Не удалось связаться с платформой.';
                }
            } finally {
                this.submitting = false;
            }
        },
        startCooldown(error) {
            this.cooldownIdentity = this.currentIdentity;
            this.cooldownUntil = cooldownDeadlineFromError(error);
            this.errorMessage = '';
            this.refreshCooldown();
            this.stopCooldownTimer();
            this.cooldownTimer = setInterval(this.refreshCooldown, 1000);
        },
        refreshCooldown() {
            this.cooldownSeconds = cooldownSecondsRemaining(this.cooldownUntil);
            if (!this.cooldownSeconds) {
                this.stopCooldownTimer();
                this.cooldownIdentity = null;
                this.cooldownUntil = 0;
            }
        },
        stopCooldownTimer() {
            if (this.cooldownTimer) clearInterval(this.cooldownTimer);
            this.cooldownTimer = null;
        }
    }
};
</script>

<style scoped>
.platform-login--embedded { display: block !important; min-height: 0 !important; }
.platform-login--embedded .platform-login__intro { display: none; }
.platform-login--embedded .platform-login__form-side { padding: 0; }
.platform-login {
    min-height: 100vh;
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(420px, .85fr);
    background: #f3f5f7;
    color: #182229;
}
.platform-login__intro {
    position: relative;
    display: flex;
    align-items: flex-end;
    overflow: hidden;
    padding: clamp(42px, 7vw, 104px);
    background:
        linear-gradient(145deg, rgba(7, 24, 31, .94), rgba(10, 58, 67, .82)),
        radial-gradient(circle at 78% 22%, #e8a735 0, transparent 32%),
        #0d3038;
    color: #fff;
}
.platform-login__intro::before,
.platform-login__intro::after {
    content: '';
    position: absolute;
    border: 1px solid rgba(255, 255, 255, .13);
    transform: rotate(-18deg);
}
.platform-login__intro::before {
    width: 70vw;
    height: 34vh;
    top: -10vh;
    right: -30vw;
}
.platform-login__intro::after {
    width: 55vw;
    height: 48vh;
    bottom: 10vh;
    left: -33vw;
}
.platform-login__intro-content { position: relative; z-index: 1; max-width: 660px; }
.platform-login__eyebrow {
    margin-bottom: 28px;
    color: #efb54e;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .22em;
}
.platform-login h1 {
    margin: 0 0 26px;
    font-size: clamp(54px, 7vw, 94px);
    line-height: .92;
    letter-spacing: -.055em;
    font-weight: 700;
}
.platform-login__intro p {
    max-width: 590px;
    margin: 0;
    color: rgba(255,255,255,.72);
    font-size: 19px;
    line-height: 1.55;
}
.platform-login__scope { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 36px; }
.platform-login__scope span {
    padding: 8px 12px;
    border: 1px solid rgba(255,255,255,.16);
    border-radius: 999px;
    color: rgba(255,255,255,.82);
    font-size: 12px;
}
.platform-login__form-side { display: flex; align-items: center; justify-content: center; padding: 48px; }
.platform-login__card { width: 100%; max-width: 420px; }
.platform-login__mark {
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    margin-bottom: 24px;
    border-radius: 14px;
    background: #123f49;
    color: #efb54e;
    font-size: 24px;
    font-weight: 800;
}
.platform-login__caption { color: #55717a; font-size: 12px; font-weight: 700; letter-spacing: .13em; text-transform: uppercase; }
.platform-login h2 { margin: 8px 0 10px; font-size: 34px; letter-spacing: -.035em; }
.platform-login__hint { margin-bottom: 30px; color: #687b82; line-height: 1.5; }
.platform-login__field { display: block; margin-bottom: 18px; }
.platform-login__field span { display: block; margin-bottom: 7px; color: #455d65; font-size: 13px; font-weight: 700; }
.platform-login__field input {
    width: 100%;
    height: 50px;
    padding: 0 14px;
    border: 1px solid #cad4d8;
    border-radius: 9px;
    background: #fff;
    color: #182229;
    outline: none;
    transition: border-color .15s, box-shadow .15s;
}
.platform-login__field input:focus { border-color: #1b6f7c; box-shadow: 0 0 0 3px rgba(27,111,124,.13); }
.platform-login__submit {
    width: 100%;
    height: 52px;
    margin-top: 8px;
    border: 0;
    border-radius: 9px;
    background: #123f49;
    color: #fff;
    font-weight: 700;
    transition: background .15s, transform .15s;
}
.platform-login__submit:hover:not(:disabled) { background: #195562; transform: translateY(-1px); }
.platform-login__submit:disabled { opacity: .65; }
.platform-login__tenant-link { display: block; margin-top: 20px; color: #54717a; text-align: center; font-size: 13px; }
@media (max-width: 900px) {
    .platform-login { grid-template-columns: 1fr; }
    .platform-login__intro { min-height: 300px; padding: 42px 28px; }
    .platform-login h1 { font-size: 52px; }
    .platform-login__intro p { font-size: 16px; }
    .platform-login__form-side { padding: 42px 24px 64px; }
}
</style>
